/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { stripPathPrefix, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useAuth } from '@/providers/auth';
import { buildAuthEvent, buildPageIdentity, consumeAuthIntent } from '@/lib/gtm/data-layer';
import { useGtm } from '@/lib/gtm/use-gtm';

/**
 * Pushes the mandatory page object on each client navigation, and a one-shot
 * login or signup event after the shopper returns from Auth0 with a registered
 * session. Signup vs login follows the intent stored on the login page.
 */
export function GtmPageTracker() {
    const location = useLocation();
    const config = useConfig();
    const { language, currency } = useSite();
    const auth = useAuth();
    const { push, consentGranted, enabled } = useGtm();
    const trackedPath = useRef<string | null>(null);
    const authSent = useRef(false);

    useEffect(() => {
        if (!enabled || !consentGranted || auth === undefined) return;
        const pathname = stripPathPrefix({ pathname: location.pathname, prefix: config.url?.prefix || '' });
        const fullPath = `${pathname}${location.search}${location.hash}`;
        if (trackedPath.current === fullPath) return;
        trackedPath.current = fullPath;
        push(
            buildPageIdentity({
                pathname,
                language,
                currency,
                shopperId: auth?.customerId || auth?.usid,
                customerId: auth?.userType === 'registered' ? auth.customerId : undefined,
                loggedIn: auth?.userType === 'registered',
            })
        );
    }, [
        auth,
        config.url?.prefix,
        consentGranted,
        currency,
        enabled,
        language,
        location.hash,
        location.pathname,
        location.search,
        push,
    ]);

    useEffect(() => {
        if (!enabled || !consentGranted || authSent.current) return;
        if (auth?.userType !== 'registered' || !auth.customerId) return;
        const intent = consumeAuthIntent();
        if (!intent) return;
        authSent.current = true;
        push(
            buildAuthEvent({
                intent,
                shopperId: auth.customerId,
                customerId: auth.customerId,
            })
        );
    }, [auth, consentGranted, enabled, push]);

    return null;
}
